/**
 * the following code is a slightly modified version of
 * https://github.com/chandlerprall/ThreeCSG/blob/master/threeCSG.es6 original license MIT the following code is
 * modified from https://github.com/chandlerprall/ThreeCSG original license: MIT
 */

const COPLANAR = 0,
    FRONT = 1,
    BACK = 2,
    SPANNING = 3;

export default class ThreeBSP {

    static EPSILON = 1e-3;

    /**
     * gibt eine Liste von BSPs zu einem Objekt oder Array von Objekten zurück. Der Objektbaum wird dabei traversiert.
     * Falls parentMatrix nicht übergeben wird, wird die Worldmatrix des Objektes genommen.
     */
    static getBSPs(o, list = [], parentMatrix = null) {
        if (Array.isArray(o)) {
            o.forEach(c => ThreeBSP.getBSPs(c, list, parentMatrix));
            return list;
        }
        let matrix = parentMatrix;
        if (matrix && o.matrix) {
            matrix = parentMatrix.clone().multiply(o.matrix);
        } else if (o.matrixWorld) {
            matrix = o.matrixWorld.clone();
        }
        if (o.bsp) {
            list.push(o.bsp);
            return list;
        } if (o.geometry) {
            let geometry = o.geometry;
            if (geometry instanceof THREE.BufferGeometry) {
                geometry = new THREE.Geometry().fromBufferGeometry(geometry);
            }
            o.bsp = new ThreeBSP(geometry, matrix);
            o.bsp.mesh = o;
            if (o.bsp.tree.divider) {
                list.push(o.bsp);
            }
            return list;
        }
        if (o.children) {
            ThreeBSP.getBSPs(o.children, list, matrix);
            return list;
        }
        return list;
    }

    constructor(geometry, matrix) {
        // Convert THREE.Geometry to ThreeBSP
        let i, _length_i,
            face, vertex, faceVertexUvs, uvs,
            polygon,
            polygons = [],
            tree;

        this.matrix = matrix;
        if (geometry instanceof ThreeBSP.Node) {
            this.tree = geometry;
            return this;
        }

        for (i = 0, _length_i = geometry.faces.length; i < _length_i; i++) {
            face = geometry.faces[i];
            faceVertexUvs = geometry.faceVertexUvs[0][i];
            polygon = new ThreeBSP.Polygon();

            if (face instanceof THREE.Face3) {
                vertex = geometry.vertices[face.a];
                uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[0].x, faceVertexUvs[0].y) : null;
                vertex = new ThreeBSP.Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[0], uvs);
                vertex.applyMatrix4(this.matrix);
                polygon.vertices.push(vertex);

                vertex = geometry.vertices[face.b];
                uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[1].x, faceVertexUvs[1].y) : null;
                vertex = new ThreeBSP.Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[2], uvs);
                vertex.applyMatrix4(this.matrix);
                polygon.vertices.push(vertex);

                vertex = geometry.vertices[face.c];
                uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[2].x, faceVertexUvs[2].y) : null;
                vertex = new ThreeBSP.Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[2], uvs);
                vertex.applyMatrix4(this.matrix);
                polygon.vertices.push(vertex);

            } else if (face instanceof THREE.Face4) {
                vertex = geometry.vertices[face.a];
                uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[0].x, faceVertexUvs[0].y) : null;
                vertex = new ThreeBSP.Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[0], uvs);
                vertex.applyMatrix4(this.matrix);
                polygon.vertices.push(vertex);

                vertex = geometry.vertices[face.b];
                uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[1].x, faceVertexUvs[1].y) : null;
                vertex = new ThreeBSP.Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[1], uvs);
                vertex.applyMatrix4(this.matrix);
                polygon.vertices.push(vertex);

                vertex = geometry.vertices[face.c];
                uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[2].x, faceVertexUvs[2].y) : null;
                vertex = new ThreeBSP.Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[2], uvs);
                vertex.applyMatrix4(this.matrix);
                polygon.vertices.push(vertex);

                vertex = geometry.vertices[face.d];
                uvs = faceVertexUvs ? new THREE.Vector2(faceVertexUvs[3].x, faceVertexUvs[3].y) : null;
                vertex = new ThreeBSP.Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[3], uvs);
                vertex.applyMatrix4(this.matrix);
                polygon.vertices.push(vertex);

            } else {
                throw 'Invalid face type at index ' + i;
            }

            polygon.materialIndex = face.materialIndex;
            polygon.calculateProperties();
            polygons.push(polygon);
        }

        this.tree = new ThreeBSP.Node(polygons);
    }

    getMaterials() {
        return Array.from(new Set(this.tree.allPolygons().map(p=>p.materialIndex)));
    }


    /**
     * um die operationen hier zu verstehen: https://github.com/evanw/csg.js/
     *
     * All CSG operations are implemented in terms of two functions,
     * clipTo() and invert(), which remove parts of a BSP tree inside
     * another BSP tree and swap solid and empty space, respectively.
     * To find the union of a and b, we want to remove everything in a inside b and
     * everything in b inside a, then combine polygons from a and b into one solid:
     *
     * a.clipTo(b);
     * b.clipTo(a);
     * a.build(b.allPolygons());
     *
     * The only tricky part is handling overlapping coplanar polygons in both trees.
     * The code above keeps both copies, but we need to keep them in one tree and
     * remove them in the other tree. To remove them from b we can clip
     * the inverse of b against a.
     * The code for union now looks like this:
     *
     * a.clipTo(b);
     * b.clipTo(a);
     * b.invert();
     * b.clipTo(a);
     * b.invert();
     * a.build(b.allPolygons());
     *
     * @param other_tree
     * @return {ThreeBSP}
     */
    union(other_tree) {
        let a = this.tree.clone(),
            b = other_tree.tree.clone();

        a.clipTo(b);
        b.clipTo(a);
        b.invert();
        b.clipTo(a);
        b.invert();
        a.build(b.allPolygons());
        a = new ThreeBSP(a, this.matrix);
        return a;
    }

    sub(other_tree) {
        let a = this.tree.clone(),
            b = other_tree.tree.clone();

        a.invert();
        a.clipTo(b);
        b.clipTo(a);
        b.invert();
        b.clipTo(a);
        b.invert();
        a.build(b.allPolygons());
        a.invert();
        a = new ThreeBSP(a, this.matrix);
        return a;
    }

    intersect(other_tree) {
        let a = this.tree.clone(),
            b = other_tree.tree.clone();

        a.invert();
        b.clipTo(a);
        b.invert();
        a.clipTo(b);
        b.clipTo(a);
        a.build(b.allPolygons());
        a.invert();
        a = new ThreeBSP(a, this.matrix);
        return a;
    }

    setMaterialIndexOnAllPolygons(materialIndex) {
        const polygons = this.tree.allPolygons();
        polygons.forEach(polygon => {
            polygon.materialIndex = materialIndex;
        });
    }

    toGeometry() {
        const matrix = new THREE.Matrix4().getInverse(this.matrix);
        const polygons = this.tree.allPolygons();
        const vertices = [];
        const normals = [];
        const uvs = [];
        polygons.sort((a,b)=>(a.materialIndex||0)-(b.materialIndex||0));
        let materialGroup = {start: 0, count: 0, materialIndex: 0};
        const materialGroups = [materialGroup];
        polygons.forEach(polygon => {
            if (polygon.materialIndex !== materialGroup.materialIndex) {
                materialGroup = {start: vertices.length/3, count: 0, materialIndex: polygon.materialIndex};
                materialGroups.push(materialGroup);
            }
            const pv = polygon.vertices.map(v => v.clone().applyMatrix4(matrix));
            const pvLength = pv.length;
            const normal = polygon.normal;
            for (let i = 2; i < pvLength; i++) {
                [pv[0], pv[i - 1], pv[i]].forEach(v => {
                    vertices.push(v.x, v.y, v.z);
                    materialGroup.count++;
                    normals.push(normal.x, normal.y, normal.z);
                    if (v.uv) {
                        uvs.push(v.uv.x, v.uv.y);
                    } else {
                        uvs.push(0, 0);
                    }
                });
            }
        });
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        g.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        if (materialGroups.length>1) {
            materialGroups.forEach(group => g.addGroup(group.start,group.count,group.materialIndex));
        }
        g.computeVertexNormals(true);

        return g;
    }

    toMesh(material) {
        const geometry = bgeometry.toGeometry();
        const mesh = new THREE.Mesh(geometry, material);

        mesh.position.setFromMatrixPosition(this.matrix);
        mesh.rotation.setFromRotationMatrix(this.matrix);

        return mesh;
    }

    setGeometryInto(mesh) {
        if (mesh) {
            // mesh.geometry.dispose();
            mesh.geometry = this.toGeometry();
            mesh.bsp = this;
        }
    }

    static Polygon = class {

        constructor(vertices = [], materialIndex = undefined) {
            this.vertices = vertices;
            this.materialIndex = materialIndex;
            if (vertices.length>0) {
                this.calculateProperties();
            }
        }

        calculateProperties() {
            const a = this.vertices[0],
                b = this.vertices[1],
                c = this.vertices[2];

            this.normal = b.clone().sub(a).cross(
                c.clone().sub(a)
            ).normalize();

            this.w = this.normal.clone().dot(a);

            return this;
        }

        clone() {
            let i, vertice_count,
                polygon = new ThreeBSP.Polygon();

            for (i = 0, vertice_count = this.vertices.length; i < vertice_count; i++) {
                polygon.vertices.push(this.vertices[i].clone());
            }
            polygon.calculateProperties();

            return polygon;
        }

        flip() {
            let i, vertices = [];

            this.normal.multiplyScalar(-1);
            this.w *= -1;

            for (i = this.vertices.length - 1; i >= 0; i--) {
                vertices.push(this.vertices[i]);
            }
            this.vertices = vertices;

            return this;
        }

        classifyVertex(vertex) {
            const side_value = this.normal.dot(vertex) - this.w;

            if (side_value < -ThreeBSP.EPSILON) {
                return BACK;
            } else if (side_value > ThreeBSP.EPSILON) {
                return FRONT;
            } else {
                return COPLANAR;
            }
        }

        classifySide(polygon) {
            let i, vertex, classification,
                num_positive = 0,
                num_negative = 0,
                vertice_count = polygon.vertices.length;

            for (i = 0; i < vertice_count; i++) {
                vertex = polygon.vertices[i];
                classification = this.classifyVertex(vertex);
                if (classification === FRONT) {
                    num_positive++;
                } else if (classification === BACK) {
                    num_negative++;
                }
            }

            if (num_positive > 0 && num_negative === 0) {
                return FRONT;
            } else if (num_positive === 0 && num_negative > 0) {
                return BACK;
            } else if (num_positive === 0 && num_negative === 0) {
                return COPLANAR;
            } else {
                return SPANNING;
            }
        }

        splitPolygon(polygon, coplanar_front, coplanar_back, front, back) {
            const classification = this.classifySide(polygon);

            if (classification === COPLANAR) {

                (this.normal.dot(polygon.normal) > 0 ? coplanar_front : coplanar_back).push(polygon);

            } else if (classification === FRONT) {

                front.push(polygon);

            } else if (classification === BACK) {

                back.push(polygon);

            } else {

                let vertice_count,
                    i, j, ti, tj, vi, vj,
                    t, v,
                    f = [],
                    b = [];

                for (i = 0, vertice_count = polygon.vertices.length; i < vertice_count; i++) {

                    j = (i + 1) % vertice_count;
                    vi = polygon.vertices[i];
                    vj = polygon.vertices[j];
                    ti = this.classifyVertex(vi);
                    tj = this.classifyVertex(vj);

                    if (ti != BACK) f.push(vi);
                    if (ti != FRONT) b.push(vi);
                    if ((ti | tj) === SPANNING) {
                        t = (this.w - this.normal.dot(vi)) / this.normal.dot(vj.clone().sub(vi));
                        v = vi.interpolate(vj, t);
                        f.push(v);
                        b.push(v);
                    }
                }


                if (f.length >= 3) front.push(new ThreeBSP.Polygon(f, polygon.materialIndex));
                if (b.length >= 3) back.push(new ThreeBSP.Polygon(b, polygon.materialIndex));
            }
        }

    };



    static Vertex = class {

        constructor(x, y, z, normal, uv) {
            this.x = x;
            this.y = y;
            this.z = z;
            this.normal = normal || new THREE.Vector3;
            this.uv = uv || new THREE.Vector2;
        }

        clone() {
            return new ThreeBSP.Vertex(this.x, this.y, this.z, this.normal.clone(), this.uv.clone());
        }

        add(vertex) {
            this.x += vertex.x;
            this.y += vertex.y;
            this.z += vertex.z;
            return this;
        }

        sub(vertex) {
            this.x -= vertex.x;
            this.y -= vertex.y;
            this.z -= vertex.z;
            return this;
        }

        multiplyScalar(scalar) {
            this.x *= scalar;
            this.y *= scalar;
            this.z *= scalar;
            return this;
        }

        cross(vertex) {
            const x = this.x,
                y = this.y,
                z = this.z;

            this.x = y * vertex.z - z * vertex.y;
            this.y = z * vertex.x - x * vertex.z;
            this.z = x * vertex.y - y * vertex.x;

            return this;
        }

        normalize() {
            const length = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);

            this.x /= length;
            this.y /= length;
            this.z /= length;

            return this;
        }

        dot(vertex) {
            return this.x * vertex.x + this.y * vertex.y + this.z * vertex.z;
        }

        lerp(a, t) {
            this.add(
                a.clone().sub(this).multiplyScalar(t)
            );

            this.normal.add(
                a.normal.clone().sub(this.normal).multiplyScalar(t)
            );

            this.uv.add(
                a.uv.clone().sub(this.uv).multiplyScalar(t)
            );

            return this;
        }

        interpolate(other, t) {
            return this.clone().lerp(other, t);
        }

        applyMatrix4( m ) {
            const x = this.x, y = this.y, z = this.z;
            const e = m.elements;

            const w = 1 / ( e[ 3 ] * x + e[ 7 ] * y + e[ 11 ] * z + e[ 15 ] );

            this.x = ( e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z + e[ 12 ] ) * w;
            this.y = ( e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z + e[ 13 ] ) * w;
            this.z = ( e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z + e[ 14 ] ) * w;

            return this;
        }

    };



    static Node = class {

        constructor(polygons) {
            let i, polygon_count,
                front = [],
                back = [];

            this.polygons = [];
            this.front = this.back = undefined;

            if (!(polygons instanceof Array) || polygons.length === 0) return;

            this.divider = polygons[0].clone();

            for (i = 0, polygon_count = polygons.length; i < polygon_count; i++) {
                this.divider.splitPolygon(polygons[i], this.polygons, this.polygons, front, back);
            }

            if (front.length > 0) {
                this.front = new ThreeBSP.Node(front);
            }

            if (back.length > 0) {
                this.back = new ThreeBSP.Node(back);
            }
        }


        isConvex(polygons) {
            let i, j;
            for (i = 0; i < polygons.length; i++) {
                for (j = 0; j < polygons.length; j++) {
                    if (i !== j && polygons[i].classifySide(polygons[j]) !== BACK) {
                        return false;
                    }
                }
            }
            return true;
        }

        build(polygons) {
            let i, polygon_count,
                front = [],
                back = [];

            if (!this.divider) {
                this.divider = polygons[0].clone();
            }

            for (i = 0, polygon_count = polygons.length; i < polygon_count; i++) {
                this.divider.splitPolygon(polygons[i], this.polygons, this.polygons, front, back);
            }

            if (front.length > 0) {
                if (!this.front) this.front = new ThreeBSP.Node();
                this.front.build(front);
            }

            if (back.length > 0) {
                if (!this.back) this.back = new ThreeBSP.Node();
                this.back.build(back);
            }
        }

        allPolygons() {
            let polygons = this.polygons.slice();
            if (this.front) polygons = polygons.concat(this.front.allPolygons());
            if (this.back) polygons = polygons.concat(this.back.allPolygons());
            return polygons;
        }

        clone() {
            const node = new ThreeBSP.Node();

            node.divider = this.divider.clone();
            node.polygons = this.polygons.map(function (polygon) {
                return polygon.clone();
            });
            node.front = this.front && this.front.clone();
            node.back = this.back && this.back.clone();

            return node;
        }

        invert() {
            let i, polygon_count, temp;

            for (i = 0, polygon_count = this.polygons.length; i < polygon_count; i++) {
                this.polygons[i].flip();
            }

            this.divider.flip();
            if (this.front) this.front.invert();
            if (this.back) this.back.invert();

            temp = this.front;
            this.front = this.back;
            this.back = temp;

            return this;
        }

        clipPolygons(polygons) {
            let i, polygon_count,
                front, back;

            if (!this.divider) return polygons.slice();

            front = [], back = [];

            for (i = 0, polygon_count = polygons.length; i < polygon_count; i++) {
                this.divider.splitPolygon(polygons[i], front, back, front, back);
            }

            if (this.front) front = this.front.clipPolygons(front);
            if (this.back) back = this.back.clipPolygons(back);
            else back = [];

            return front.concat(back);
        }

        clipTo(node) {
            this.polygons = node.clipPolygons(this.polygons);
            if (this.front) this.front.clipTo(node);
            if (this.back) this.back.clipTo(node);
        }

    };

    static Plane = class {

        constructor(normal, w) {
            this.normal = normal;
            this.w = w;
        }

        clone() {
            return new THREE.BSPTree.Plane(this.normal.clone(), this.w);
        }

        flip() {
            this.normal = this.normal.clone().negate();
            this.w = -this.w;
        }

        splitPolygon(polygon, coplanarFront, coplanarBack, front, back) {

            // Classify each point as well as the entire polygon into one of the above
            // four classes.
            let polygonType = 0,
                types = [],
                vertices = [polygon.a, polygon.b, polygon.c];

            let t, type, face;

            for (let i = 0; i < vertices.length; i++) {
                t = this.normal.dot(vertices[i].position) - this.w;
                type = (t < -ThreeBSP.EPSILON) ? BACK : (t > ThreeBSP.EPSILON) ? FRONT : COPLANAR;
                polygonType |= type;
                types.push(type);
            }

            // Put the polygon in the correct list, splitting it when necessary.
            switch (polygonType) {
                case COPLANAR:
                    (this.normal.dot(polygon.normal) > 0 ? coplanarFront : coplanarBack).push(polygon);
                    break;
                case FRONT:
                    front.push(polygon);
                    break;
                case BACK:
                    back.push(polygon);
                    break;
                case SPANNING:
                    let f = [], b = [];
                    for (let i = 0; i < vertices.length; i++) {
                        let j = (i + 1) % vertices.length;
                        let ti = types[i], tj = types[j];
                        let vi = vertices[i], vj = vertices[j];
                        if (ti != BACK) f.push(vi);
                        if (ti != FRONT) b.push(ti != BACK ? vi.clone() : vi);
                        if ((ti | tj) == SPANNING) {
                            let t = (this.w - this.normal.dot(vi.position)) / this.normal.dot(vj.position.clone().subSelf(vi.position));
                            let v = vi.interpolate(vj, t);
                            f.push(v.clone());
                            b.push(v.clone());
                        }
                    }

                    if (f.length >= 3) {
                        for (let i = 2; i < f.length; i++) {
                            face = new THREE.Face3(
                                f[0], f[i - 1], f[i],
                                f[0].normal
                            );
                            front.push(face);
                        }
                    }

                    if (b.length >= 3) {
                        for (let i = 2; i < b.length; i++) {
                            face = new THREE.Face3(
                                b[0], b[i - 1], b[i],
                                b[0].normal
                            );
                            back.push(face);
                        }
                    }

                    break;
            }
        }

    };


}


